/**
 * Reading and writing spreadsheets in the browser.
 *
 * ## Why here and not on the server
 *
 * Parsing untrusted spreadsheets is a real attack surface — zip bombs, entity
 * expansion, and a long CVE history in every library that does it. Doing it in
 * the tab of the person who chose the file confines the damage to them, instead
 * of the API process every tenant shares. It costs nothing in security: the API
 * validates every row anyway, because anyone can POST to it directly.
 *
 * ## Why exceljs and not SheetJS
 *
 * The prototype uses SheetJS from a CDN. On npm, `xlsx` is frozen at 0.18.5 with
 * two HIGH advisories whose patched version is listed as "none" — SheetJS moved
 * distribution to their own registry at 0.19+. Installing it would knowingly add
 * unpatched prototype-pollution and ReDoS. Their own registry is an option but
 * is not lockfile- or Dependabot-friendly. exceljs 4.4.0 has no open advisories.
 * See docs/DECISIONS.md §2.
 *
 * ## Loaded on demand
 *
 * exceljs is around a megabyte. `import()` keeps it in its own chunk so the
 * clinic staff who never touch import/export never download it.
 */
import {
  TEMPLATE_HEADERS,
  TEMPLATE_ROWS,
  parseImportDate,
  parseImportInt,
  parseImportMoneyPence,
  pickCell,
  sanitiseForSpreadsheet,
} from '@inventory-tracker/core';
import type { ImportRow, ProductRow } from '../lib/api.js';

/**
 * exceljs wraps some cells: a formula cell carries `{ formula, result }`, a
 * styled one `{ richText: [...] }`, a link `{ text, hyperlink }`. We want what
 * the person looking at the sheet sees.
 */
function unwrapCell(value: unknown): unknown {
  if (value === null || value === undefined || typeof value !== 'object') return value;
  if (value instanceof Date) return value;
  if ('result' in value) return (value as { result: unknown }).result;
  if ('richText' in value) {
    return (value as { richText: { text: string }[] }).richText.map((t) => t.text).join('');
  }
  if ('text' in value) return (value as { text: unknown }).text;
  return value;
}

/** Rows as they come off a sheet, before the API validates them. */
type RawRow = Record<string, unknown>;

async function excel() {
  return (await import('exceljs')).default ?? (await import('exceljs'));
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** The ready-made import template — spec §6, "Download template". */
export async function downloadTemplate(): Promise<void> {
  const ExcelJS = await excel();
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Stock');

  ws.addRow([...TEMPLATE_HEADERS]);
  for (const row of TEMPLATE_ROWS) ws.addRow([...row]);

  ws.getRow(1).font = { bold: true };
  ws.columns = TEMPLATE_HEADERS.map((h) => ({ width: Math.max(13, h.length + 6) }));

  const buffer = await wb.xlsx.writeBuffer();
  download(new Blob([buffer], { type: XLSX_MIME }), 'stock-import-template.xlsx');
}

/**
 * Reads the first sheet of a .xlsx/.csv into rows keyed by header text.
 *
 * Header keys are kept exactly as typed; `pickCell` in core does the
 * case-insensitive synonym matching, so this function stays purely mechanical.
 */
export async function readSpreadsheet(file: File): Promise<RawRow[]> {
  const ExcelJS = await excel();
  const wb = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();

  if (file.name.toLowerCase().endsWith('.csv')) {
    // exceljs's CSV reader wants a stream; the text path is simpler and avoids
    // pulling a Node stream shim into the browser bundle.
    return readCsv(await file.text());
  }

  await wb.xlsx.load(buffer);
  const ws = wb.worksheets[0];
  if (!ws) return [];

  const headerRow = ws.getRow(1);
  const headers: string[] = [];
  headerRow.eachCell({ includeEmpty: true }, (cell, col) => {
    headers[col - 1] = String(cell.value ?? '').trim();
  });

  const rows: RawRow[] = [];
  ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber === 1) return;
    const obj: RawRow = {};
    row.eachCell({ includeEmpty: true }, (cell, col) => {
      const key = headers[col - 1];
      if (!key) return;
      obj[key] = unwrapCell(cell.value);
    });
    rows.push(obj);
  });

  return rows;
}

/**
 * A small CSV reader. Handles quoted fields containing commas, quotes and
 * newlines — the three things that break a naive `split(',')` on real exports.
 */
export function readCsv(text: string): RawRow[] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  const src = text.replace(/^\uFEFF/, ''); // strip the BOM Excel writes

  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 1;
        } else inQuotes = false;
      } else field += ch;
      continue;
    }
    /**
     * A quote only opens a quoted field at the START of that field. Excel and
     * RFC 4180 both treat one appearing mid-field as an ordinary character —
     * without this, `5" needle` or `=HYPERLINK("a","b")` swallows the following
     * comma and silently shifts every remaining column of the row.
     */
    if (ch === '"' && field === '') inQuotes = true;
    else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i += 1;
      row.push(field);
      field = '';
      rows.push(row);
      row = [];
    } else field += ch;
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  const [header, ...body] = rows.filter((r) => r.some((c) => c.trim() !== ''));
  if (!header) return [];

  return body.map((cells) => {
    const obj: RawRow = {};
    header.forEach((h, i) => {
      const key = h.trim();
      if (key) obj[key] = cells[i] ?? '';
    });
    return obj;
  });
}

/**
 * Turns raw sheet rows into the shape the API accepts, dropping rows with no
 * product name — a blank line in the middle of a sheet is normal, not an error.
 */
export function toImportRows(raw: RawRow[]): ImportRow[] {
  const out: ImportRow[] = [];

  for (const r of raw) {
    const name = String(pickCell(r, 'name') ?? '').trim();
    if (!name) continue;

    const row: ImportRow = { name };

    const brand = pickCell(r, 'brand');
    if (brand !== null && brand !== undefined && String(brand).trim() !== '') row.brand = String(brand).trim();

    const category = pickCell(r, 'category');
    if (category !== null && category !== undefined && String(category).trim() !== '')
      row.category = String(category).trim();

    const classification = pickCell(r, 'classification');
    if (classification !== null && classification !== undefined && String(classification).trim() !== '')
      row.classification = String(classification).trim();

    const lot = pickCell(r, 'lot');
    if (lot !== null && lot !== undefined && String(lot).trim() !== '') row.lot = String(lot).trim();

    row.qty = parseImportInt(pickCell(r, 'qty'), 0);
    row.minLevel = parseImportInt(pickCell(r, 'minLevel'), 0);

    const cost = parseImportMoneyPence(pickCell(r, 'unitCostPence'));
    if (cost !== null) row.unitCostPence = cost;

    const retail = parseImportMoneyPence(pickCell(r, 'retailPricePence'));
    if (retail !== null) row.retailPricePence = retail;

    const expiry = parseImportDate(pickCell(r, 'expiry'));
    if (expiry) row.expiry = expiry;

    out.push(row);
  }

  return out;
}


export const EXPORT_HEADERS = [
  'Product',
  'Brand',
  'Category',
  'Type',
  'Qty',
  'Min level',
  'Unit cost',
  'Stock value',
  'Stock status',
  'Earliest expiry',
  'Expiry status',
] as const;

/**
 * The exported cells, separated from the file writing so the sanitising can be
 * tested without a DOM. Every text column goes through `sanitiseForSpreadsheet`;
 * a product name beginning `=` must not become a live formula in the owner's
 * Excel, and the only way to be sure of that is to assert on the actual rows.
 */
export function buildExportRows(rows: ProductRow[]): (string | number)[][] {
  return rows.map((p) => [
    sanitiseForSpreadsheet(p.name),
    sanitiseForSpreadsheet(p.brand ?? ''),
    sanitiseForSpreadsheet(p.category ?? ''),
    sanitiseForSpreadsheet(p.classificationLabel ?? ''),
    p.qtyOnHand,
    p.minLevel,
    // Pounds, so the figures are usable in a spreadsheet without dividing.
    p.unitCostPence / 100,
    p.stockValuePence / 100,
    sanitiseForSpreadsheet(p.status.stockLabel),
    p.earliestExpiry ?? '',
    sanitiseForSpreadsheet(p.status.expiryLabel),
  ]);
}

/**
 * Exports the current product list — spec §6, "Export to Excel".
 *
 * Every text cell goes through `sanitiseForSpreadsheet`. Product names are typed
 * by clinic staff and the export is usually opened by someone else, so a name
 * beginning `=` must not become a live formula in the owner's Excel.
 */
export async function exportProducts(rows: ProductRow[], clinicName: string): Promise<void> {
  const ExcelJS = await excel();
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Stock');

  ws.addRow([...EXPORT_HEADERS]);
  ws.getRow(1).font = { bold: true };
  for (const row of buildExportRows(rows)) ws.addRow(row);

  ws.columns = EXPORT_HEADERS.map((h, i) => ({
    width: i === 0 ? 30 : Math.max(12, h.length + 4),
  }));
  ws.getColumn(7).numFmt = '£#,##0.00';
  ws.getColumn(8).numFmt = '£#,##0.00';

  const stamp = new Date().toISOString().slice(0, 10);
  const safeName = clinicName.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '') || 'stock';

  const buffer = await wb.xlsx.writeBuffer();
  download(new Blob([buffer], { type: XLSX_MIME }), `${safeName}-stock-${stamp}.xlsx`);
}
